import { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@myRedux/hooks';
import { ApiCallStatus } from '@myRedux/networkState';
import { createSessionAsync, selectSession } from '@myRedux/session/sessionSlice';
import { createUserResourceAsync, selectSignUpUser } from '@myRedux/user/userSlice';
import { selectApiRoot } from '@myRedux/api/apiSlice';
import { authorizeGoogleUser, OAuthRequest } from '@myRedux/oauth/google/authorizeSlice';
import { wasSuccessfullyFulfilledDispatch } from '@myRedux/dispatchUtilities';
import { TextField, TextFieldProps } from '@componentLibraryWrapper/textfield';
import { ButtonSubmit } from '@commonComponent/buttonSubmit';
import { Error } from '@commonComponent/error';
import GridContainer from '@commonComponent/grid/gridContainer';
import GridElement from '@commonComponent/grid/gridElement';
import GridDivider from '@commonComponent/grid/gridDivider';
import { Box } from '@componentLibraryWrapper/box';
import { Button } from '@componentLibraryWrapper/button';
import { useTheme } from 'hooks/mui/useTheme';
import { EMAILVERIFIEDBYANOTHERUSER, USERNAMENOTUNIQUE } from 'api/errorTags';
import './login.scss';

interface LoginProps {
  onLoginSuccess: () => void;
}

const Login = ({
  onLoginSuccess
}: LoginProps) => {
  const loginUsername = useRef<TextFieldProps>();
  const loginPassword = useRef<TextFieldProps>();
  const signupUsername = useRef<TextFieldProps>();
  const signupPassword = useRef<TextFieldProps>();
  const signupRetypePassword = useRef<TextFieldProps>();
  
  const [signupRetypePasswordErrorHelperText, setSignupRetypePasswordErrorHelperText] = useState<string>();
  const [signupUsernameErrorText, setSignupUsernameErrorText] = useState<string>();
  
  const apiSelector = useAppSelector(selectApiRoot);
  const sessionSelector = useAppSelector(selectSession);
  const signUpUserSelector = useAppSelector(selectSignUpUser)
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const location = useLocation();

  async function handleLoginSubmit(event: React.SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();

    await login(loginUsername.current?.value, loginPassword.current?.value);
  }

  async function handleSignupSubmit(event: React.SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();

    if (signupPassword.current?.value !== signupRetypePassword.current?.value) {
      setSignupRetypePasswordErrorHelperText("Passwords don't match");
      return;
    }

    const userResource = {
      userName: signupUsername.current?.value,
      password: signupPassword.current?.value
    };
    const dispatchResult = await dispatch(createUserResourceAsync({resourceWithPostRel: apiSelector.value!, resourceToPost: userResource}));
    if (wasSuccessfullyFulfilledDispatch(dispatchResult)) {
      await login(userResource.userName, userResource.password);
    }
    else {
      if (dispatchResult.payload === EMAILVERIFIEDBYANOTHERUSER) {
        setSignupUsernameErrorText('Email verified by another account');
      }
      if (dispatchResult.payload === USERNAMENOTUNIQUE) {
        setSignupUsernameErrorText('Username taken by another user');
      }
    }
  }

  async function login(userName: string, password: string) {
    const postBody = {
      userName: userName,
      password: password,
    };
    const payload = await dispatch(createSessionAsync({resourceWithPostRel: apiSelector.value!, resourceToPost: postBody}));
    if (wasSuccessfullyFulfilledDispatch(payload)) {
      onLoginSuccess()
    }
  }

  function handleSignupRetypePasswordChange() {
    if (signupRetypePasswordErrorHelperText
      && signupPassword.current?.value === signupRetypePassword.current?.value) {
      setSignupRetypePasswordErrorHelperText(undefined);
    }
  }

  function signupUsernameOnChange() {
    if (signupUsernameErrorText) {
      setSignupUsernameErrorText(undefined);
    }
  }

  async function googleOnClick() {
    const redirectUrl = `${location.pathname}${location.search}`;
    const response = await dispatch(authorizeGoogleUser(new OAuthRequest(redirectUrl)));
    if (wasSuccessfullyFulfilledDispatch(response)) {
      window.location.href = response.payload;
    }
  }

  return (
    <GridElement
      xs={12}
      sm={12}
      md={10}
      lg={6}
      xl={5}
      id='login-box'
    >
      <Box
        sx={{
          backgroundColor: theme.palette.background.default
        }}>
        <GridContainer>
          <GridElement
            xs={12}
            sm={6}
            md={6}
            lg={6}
            xl={6}
            className='signup'
          >
            <form
              onSubmit={handleSignupSubmit}>
            <h2>CREATE ACCOUNT</h2>
              <TextField
                error={!!signupUsernameErrorText}
                helperText={signupUsernameErrorText}
                inputRef={signupUsername}
                onChange={signupUsernameOnChange}
                required
                inputProps={{
                  maxLength:50
                }}
                label="Username or Email"
                fullWidth
              />
              <TextField
                inputRef={signupPassword}
                required
                inputProps={{
                  minLength:8
                }}
                label="Password"
                type="password"
                fullWidth
              />
              <TextField
                error={!!signupRetypePasswordErrorHelperText}
                helperText={signupRetypePasswordErrorHelperText}
                inputRef={signupRetypePassword}
                onBlur={handleSignupRetypePasswordChange}
                required
                label="Re-type Password"
                type="password"
                fullWidth
              />
              <ButtonSubmit
                submitStatus={signUpUserSelector.status}
                id='signupnewuserbutton'
                color='primary'
                variant='contained'>
                Signup
              </ButtonSubmit>
            </form>
            {signUpUserSelector.status === ApiCallStatus.Failed &&
              <Error>
                Error creating account. Please try again.
              </Error>}
          </GridElement>
          <GridDivider
            orientation='vertical'
            variant='middle'
            flexItem/>
          <GridElement
            xs={12}
            sm={6}
            md={6}
            lg={6}
            xl={6}
            className='login'
          >
            <form
              onSubmit={handleLoginSubmit}>
              <h2>LOG IN</h2>
              <TextField
                inputRef={loginUsername}
                required
                label="User Name"
                fullWidth
              />
              <TextField
                inputRef={loginPassword}
                required
                label="Password"
                type="password"
                fullWidth
              />
              <Box>
                <ButtonSubmit
                  submitStatus={sessionSelector.status}
                  id='loginsubmitbutton'
                  color='primary'
                  variant='contained'>
                  Login
                </ButtonSubmit>
              </Box>
              <Button
                href={'forgotpassword'}>
                Forgot Password
              </Button>
            </form>
            {sessionSelector.status === ApiCallStatus.Failed &&
              <Error>
                Error logging in. Please try again
              </Error>}
            <Button
              onClick={googleOnClick}>
                Google
            </Button>
          </GridElement>
        </GridContainer>
      </Box>
    </GridElement>
  );
}

export default Login;